import api from './api'

/**
 * The company portal API.
 *
 * A COMPANY_ADMIN is refused `/api/admin/*` outright, so this is a separate,
 * deliberately narrow client rather than a filtered view of adminService.
 */

export interface CompanyProfileResponse {
  company: {
    id: string
    name: string
    registrationNumber: string | null
    contactName: string
    contactEmail: string
    contactPhone: string | null
    address: string | null
    serviceZones: string[]
    status: 'ACTIVE' | 'SUSPENDED'
    commissionRate: number
    createdAt: string
  }
  collectors: { total: number; onDuty: number; offDuty: number }
  metrics: {
    totalPickups: number
    completedPickups: number
    missedPickups: number
    activePickups: number
    completionRate: number
    pendingOffers: number
    averageRating: number
    totalRatings: number
  }
  last30Days: { total: number; completed: number; missed: number; active: number }
}

export interface CompanyCollectorRow {
  id: string
  name: string
  email: string
  phone: string | null
  zone: string | null
  isActive: boolean
  isAvailable: boolean
  maxDailyPickups: number
  approvalStatus: string | null
  createdAt: string
  metrics: {
    totalPickups: number
    completedPickups: number
    missedPickups: number
    activePickups: number
    pendingOffers: number
    averageRating: number
    totalRatings: number
    completionRate: number
  }
}

export interface CompanyEarningsResponse {
  payRate: number
  commissionRate: number
  completedCount: number
  totalEarned: number
  totalPaidOut: number
  pendingPayouts: number
  availableBalance: number
  companyShare: number
  collectorShare: number
  currency: string
  collectorCount: number
  settlement?: string
}

export async function fetchCompanyProfile(): Promise<CompanyProfileResponse> {
  const res = await api.get('/company/profile')
  return res.data?.data ?? res.data
}

export async function fetchCompanyCollectors(): Promise<{ collectors: CompanyCollectorRow[] }> {
  const res = await api.get('/company/collectors')
  return res.data?.data ?? res.data
}

export async function updateCompanyCollector(
  collectorId: string,
  payload: { isAvailable?: boolean; maxDailyPickups?: number },
) {
  const res = await api.patch(`/company/collectors/${collectorId}`, payload)
  return res.data?.data ?? res.data
}

export async function fetchCompanyPickups(params?: { status?: string; page?: number; limit?: number }) {
  const res = await api.get('/company/pickups', { params })
  return res.data?.data ?? res.data
}

export async function fetchCompanyAssignments(params?: { status?: string; limit?: number }) {
  const res = await api.get('/company/assignments', { params })
  return res.data?.data ?? res.data
}

export async function fetchCompanyEarnings(): Promise<CompanyEarningsResponse> {
  const res = await api.get('/company/earnings')
  return res.data?.data ?? res.data
}

// ── Payouts ────────────────────────────────────────────────────────
export type PayoutStatus = 'PENDING' | 'APPROVED' | 'PROCESSING' | 'COMPLETED' | 'REJECTED' | 'FAILED'

export interface CompanyPayoutAccount {
  id: string
  method: string
  accountName: string
  accountNumber: string
  bankName: string | null
  isDefault: boolean
  isVerified: boolean
  createdAt: string
}

export interface CompanyBalance {
  grossEarned: number
  paidToCollectors: number
  committedToCollectors: number
  paidToCompany: number
  committedToCompany: number
  availableToCompany: number
}

export interface CompanyPayoutRequest {
  id: string
  amount: number
  currency: string
  method: string
  status: PayoutStatus
  adminNote: string | null
  failureReason: string | null
  createdAt: string
  processedAt: string | null
  completedAt: string | null
  account?: { accountName: string; bankName: string | null }
}

export interface CompanyPayoutsResponse {
  commissionRate: number
  balance: CompanyBalance
  requests: CompanyPayoutRequest[]
  currency: string
}

export async function fetchCompanyPayoutAccounts(): Promise<{ accounts: CompanyPayoutAccount[] }> {
  const res = await api.get('/company/payout-accounts')
  return res.data?.data ?? res.data
}

export async function createCompanyPayoutAccount(payload: {
  method: string
  accountName: string
  accountNumber: string
  bankCode?: string
  bankName?: string
}) {
  const res = await api.post('/company/payout-accounts', payload)
  return res.data?.data ?? res.data
}

export async function setDefaultCompanyPayoutAccount(accountId: string) {
  const res = await api.patch(`/company/payout-accounts/${accountId}/default`)
  return res.data?.data ?? res.data
}

export async function deleteCompanyPayoutAccount(accountId: string) {
  const res = await api.delete(`/company/payout-accounts/${accountId}`)
  return res.data?.data ?? res.data
}

export async function fetchCompanyPayouts(): Promise<CompanyPayoutsResponse> {
  const res = await api.get('/company/payouts')
  return res.data?.data ?? res.data
}

export async function requestCompanyPayout(amount: number, accountId: string) {
  const res = await api.post('/company/payouts', { amount, accountId })
  return res.data?.data ?? res.data
}
